import { makeStyles } from 'design-system/product/theme';
import { twMerge } from 'tailwind-merge';
import {
  ValidatedImagesResponseDto,
  MultimodalAssetDto,
  convertImageMimeTypeToExtension,
} from '@scale/mm-shared';
import { Spinner, Button } from '@scale/scaleui';

type Props = {
  imageData?: ValidatedImagesResponseDto;
  disabled?: boolean;
  setFocusedImage: (image: MultimodalAssetDto) => void;
  page: number;
  setPage: (page: number) => void;
  isLoading: boolean;
  isError: boolean;
};

const useStyles = makeStyles(() => ({
  imageContainer: {
    width: '400px',
    height: '400px',
    backgroundColor: 'white',
    '&:hover': {
      cursor: 'pointer',
      transform: 'scale(1.05)',
      boxShadow: '0 0 15px 2px rgba(0, 0, 0, 0.4)',
    },
  },
}));

const SerpApiImageSearchResults = ({
  imageData,
  disabled,
  setFocusedImage,
  page,
  setPage,
  isLoading,
  isError,
}: Props) => {
  const classes = useStyles();
  if (isLoading) {
    return (
      <div className="flex justify-center items-center gap-4">
        <p>Loading internet results</p>
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div
      className={`flex flex-wrap p-4 justify-center w-full max-h-full overflow-auto scale-tailwind gap-4`}
    >
      {isError || !imageData?.images.length ? (
        <div className="w-full text-center text-2xl mt-2">
          {page > 0
            ? 'No more Internet Images available.'
            : 'No valid internet results found. Please try another search.'}
        </div>
      ) : (
        <div className="flex flex-wrap gap-4">
          {imageData.images.map(image => (
            <div
              key={image.url}
              className={twMerge(
                'flex items-center justify-center overflow-hidden border border-neutral-100 rounded-lg shadow-lg',
                classes.imageContainer,
              )}
              data-testid="image-results-container"
              onClick={() => {
                if (disabled) {
                  return;
                }
                const { hostname } = new URL(image.url);

                // hack. we need to provide a multimodal asset dto even though no asset exists
                setFocusedImage({
                  image_url: image.url,
                  image_domain: hostname,
                  image_size: image.contentLength ?? 0,
                  image_dimensions: image.imageData,
                  image_extension: convertImageMimeTypeToExtension(image.contentType),
                  image_hashes: { sha256: image.sha256 },
                  image_licenses: [],
                  image_category: [],
                  ingestion_date: new Date(),
                  invalid_hash: false,
                  image_source_url: image.sourceUrl,
                  image_license_url: image.licenseUrl,
                });
              }}
            >
              <img src={image.url} className="object-cover max-w-full h-auto" />
            </div>
          ))}
        </div>
      )}
      <div className="w-full flex justify-center items-center gap-4 mt-4">
        <Button onClick={() => setPage(page - 1)} disabled={page === 0}>
          Prev
        </Button>
        <Button
          onClick={() => setPage(page + 1)}
          disabled={isLoading || isError || !imageData?.images.length}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default SerpApiImageSearchResults;
